import * as THREE from 'three';
import type { SketchSolid, SketchFace, SketchVec3 } from './types';

/**
 * Solid topology — DERIVED structure, recomputed on demand.
 *
 * Sketch semantics live on shared edges: two faces meeting along the same
 * edge move together (Push/Pull), a face drawn inside another splits it, and
 * erasing an edge can delete its faces. Faces store analytic loops; this
 * module derives the deduplicated edge graph (segment soup → shared edges)
 * with endpoint hashing at tight epsilon.
 */

const EPS = 1e-5;

interface EdgeKeyPoint { x: number; y: number; z: number }

const keyOf = (p: THREE.Vector3): string =>
    `${Math.round(p.x / EPS)}|${Math.round(p.y / EPS)}|${Math.round(p.z / EPS)}`;

const keyOfVec = (p: SketchVec3): string =>
    keyOf(new THREE.Vector3(...p));

/** Weld key of one contour point (shared across the subobject editors so a
 *  moved face stretches exactly the edges topology sees as shared). */
export const pointKey = keyOfVec;

/** Weld key of a live THREE.Vector3 point (same key space as pointKey). */
export const pointKeyOfVector = keyOf;

/** One shared straight edge (curved face borders decompose into segments). */
export interface SharedEdge {
    id: string;
    a: THREE.Vector3;
    b: THREE.Vector3;
    /** Faces that own this edge, by face id. */
    faceIds: string[];
    /** Solids that own the faces (cross-solid shared edges stay per-solid). */
    solidIds: string[];
}

export interface SolidTopology {
    edges: Map<string, SharedEdge>;
    /** Face id → its edge keys. */
    faceEdges: Map<string, string[]>;
}

/**
 * Build the shared-edge topology of one solid. Contour closing segments
 * (last→first point of each loop) are included, so a rectangular face
 * yields exactly 4 edges.
 */
export function buildTopology(solid: SketchSolid): SolidTopology {
    const edges = new Map<string, SharedEdge>();
    const faceEdges = new Map<string, string[]>();

    const addSegment = (a: THREE.Vector3, b: THREE.Vector3, face: SketchFace): void => {
        if (a.distanceToSquared(b) < EPS * EPS) return;
        const ka = keyOf(a);
        const kb = keyOf(b);
        const id = ka < kb ? `${ka}~${kb}` : `${kb}~${ka}`;
        let edge = edges.get(id);
        if (!edge) {
            edge = { id, a, b, faceIds: [], solidIds: [] };
            edges.set(id, edge);
        }
        if (!edge.faceIds.includes(face.id)) edge.faceIds.push(face.id);
        if (!edge.solidIds.includes(solid.id)) edge.solidIds.push(solid.id);
    };

    for (const face of solid.faces) {
        const keys: string[] = [];
        const addLoop = (loop: SketchVec3[]): void => {
            for (let i = 0; i < loop.length; i++) {
                const a = new THREE.Vector3(...loop[i]);
                const b = new THREE.Vector3(...loop[(i + 1) % loop.length]);
                const ka = keyOf(a);
                const kb = keyOf(b);
                const id = ka < kb ? `${ka}~${kb}` : `${kb}~${ka}`;
                if (!keys.includes(id)) keys.push(id);
                addSegment(a, b, face);
            }
        };
        addLoop(face.outer);
        face.holes.forEach(addLoop);
        faceEdges.set(face.id, keys);
    }

    return { edges, faceEdges };
}

/** Resolve one shared edge of a solid by its topology key (null = the key
 *  predates the current loops — the selection is stale). */
export function solidSharedEdge(solid: SketchSolid, edgeKey: string): SharedEdge | null {
    return buildTopology(solid).edges.get(edgeKey) ?? null;
}

/** Face ids owning the given shared edges (union, order-stable). */
export function facesOfEdges(solid: SketchSolid, edgeKeys: string[]): string[] {
    const topo = buildTopology(solid);
    const out: string[] = [];
    for (const key of edgeKeys) {
        const edge = topo.edges.get(key);
        if (!edge) continue;
        edge.faceIds.forEach((id) => {
            if (!out.includes(id)) out.push(id);
        });
    }
    return out;
}

/** Split a SharedEdge id back into its two endpoint weld keys. */
const splitEdgeId = (id: string): [string, string] => {
    const idx = id.indexOf('~');
    return [id.slice(0, idx), id.slice(idx + 1)];
};

/**
 * The connected "curve" an edge belongs to (Sketch's Curve entity): the
 * maximal run of shared edges joined end-to-end through 2-VALENT corners —
 * corners where exactly two edges meet. A box corner (three edges) always
 * splits the run, so box edges stay individual picks; a circle or arc
 * polyline (every interior corner 2-valent) is ONE piece — picking any
 * segment selects, highlights and moves the whole curve.
 */
export function edgeChainKeys(solid: SketchSolid, edgeKey: string): string[] {
    const topo = buildTopology(solid);
    if (!topo.edges.has(edgeKey)) return [];

    const incident = new Map<string, string[]>();
    topo.edges.forEach((_edge, id) => {
        const [ka, kb] = splitEdgeId(id);
        const add = (endpoint: string): void => {
            const list = incident.get(endpoint);
            if (list) list.push(id);
            else incident.set(endpoint, [id]);
        };
        add(ka);
        add(kb);
    });

    const chain = new Set<string>([edgeKey]);
    const queue = [edgeKey];
    while (queue.length > 0) {
        const id = queue.pop()!;
        const [ka, kb] = splitEdgeId(id);
        for (const endpoint of [ka, kb]) {
            const neighbors = incident.get(endpoint) ?? [];
            // Only a 2-valent corner continues the curve; junctions where
            // three or more edges meet are hard boundaries.
            if (neighbors.length !== 2) continue;
            const next = neighbors[0] === id ? neighbors[1] : neighbors[0];
            if (!chain.has(next)) {
                chain.add(next);
                queue.push(next);
            }
        }
    }
    return Array.from(chain);
}

/** All unique corner points of a solid (endpoint inference pool). */
export function solidVertices(solid: SketchSolid): THREE.Vector3[] {
    const seen = new Map<string, THREE.Vector3>();
    const visit = (loop: SketchVec3[]): void => {
        for (const p of loop) {
            const k = keyOfVec(p);
            if (!seen.has(k)) seen.set(k, new THREE.Vector3(...p));
        }
    };
    for (const face of solid.faces) {
        visit(face.outer);
        face.holes.forEach(visit);
    }
    return Array.from(seen.values());
}

/**
 * Live contour points of ONE welded corner, by point key. Every face loop
 * corner hashing to the key comes back (they are the same world point —
 * this is what a vertex move feeds into the weld pool). Empty when the key
 * predates the current loops: the selection is stale and dies.
 */
export function solidVertexPoints(solid: SketchSolid, vertexKey: string): SketchVec3[] {
    const pts: SketchVec3[] = [];
    const visit = (loop: SketchVec3[]): void => {
        for (const p of loop) {
            if (keyOfVec(p) === vertexKey) pts.push(p);
        }
    };
    for (const face of solid.faces) {
        visit(face.outer);
        face.holes.forEach(visit);
    }
    return pts;
}

/** Face centroid (area-weighted simple average of the outer loop). */
export function faceCentroid(face: SketchFace): THREE.Vector3 {
    const pts = face.outer.map((p) => new THREE.Vector3(...p));
    if (pts.length === 0) return new THREE.Vector3(...face.origin);
    const sum = pts.reduce((acc, p) => acc.add(p), new THREE.Vector3());
    return sum.divideScalar(pts.length);
}
